/**
 * useDeletePurchase — mutation for `DELETE /api/v1/me/purchases/:id`.
 *
 * Permanently deletes a purchase (server decides hard-delete vs soft-hide per SKU
 * and purges generated R2 files). On success, invalidates the purchases query.
 * A 409 (result still generating) is surfaced as Error('in_flight').
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../lib/api-client';
import { useAuth } from '../store/auth';

export function useDeletePurchase(): {
  mutate: (id: string) => void;
  mutateAsync: (id: string) => Promise<void>;
  isPending: boolean;
  isError: boolean;
} {
  const jwt = useAuth((s) => s.jwt);
  const qc = useQueryClient();

  const m = useMutation<void, Error, string>({
    mutationFn: async (id) => {
      if (!jwt) throw new Error('not_authenticated');
      const res = await apiFetch(`/api/v1/me/purchases/${encodeURIComponent(id)}`, jwt, {
        method: 'DELETE',
      });
      if (res.status === 409) throw new Error('in_flight');
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`delete_purchase_failed: ${res.status} ${text}`);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['me-purchases'] });
    },
  });

  return { mutate: m.mutate, mutateAsync: m.mutateAsync, isPending: m.isPending, isError: m.isError };
}
